import { type JSX } from 'react'
import type { ActiveWhatsAppCall, WhatsAppCallState } from '../../hooks/useWhatsAppCall.js'
import { WhatsAppIcon } from '../conversations/BrandIcons.js'
import { Phone, PhoneOff, Mic, MicOff } from 'lucide-react'

interface Props {
  state: WhatsAppCallState
  call: ActiveWhatsAppCall | null
  muted: boolean
  durationSeconds: number
  error: string | null
  onAccept: () => void
  onReject: () => void
  onHangUp: () => void
  onToggleMute: () => void
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

const STATUS_LABEL: Record<WhatsAppCallState, string> = {
  idle: '',
  incoming: 'Incoming WhatsApp call',
  dialing: 'Calling…',
  ringing: 'Ringing…',
  connecting: 'Connecting…',
  connected: 'Connected',
  ended: 'Call ended',
  failed: 'Call failed',
}

export function WhatsAppCallOverlay({
  state,
  call,
  muted,
  durationSeconds,
  error,
  onAccept,
  onReject,
  onHangUp,
  onToggleMute,
}: Props): JSX.Element | null {
  if (state === 'idle' || !call) return null

  const name = call.display_name || call.profile_name || `+${call.wa_id}`
  const isIncoming = state === 'incoming'

  return (
    <div className="fixed bottom-6 right-6 z-50 w-72 overflow-hidden rounded-2xl bg-[#075E54] text-white shadow-2xl">
      <div className="flex flex-col items-center px-5 py-6">
        <div
          className={`mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-white/15 ${
            state === 'ringing' || state === 'incoming' || state === 'dialing' ? 'animate-pulse' : ''
          }`}
        >
          <WhatsAppIcon className="h-8 w-8 text-[#25D366]" />
        </div>
        <p className="max-w-full truncate text-sm font-semibold">{name}</p>
        <p className="mt-0.5 text-[11px] text-white/70">
          {state === 'connected' ? formatDuration(durationSeconds) : STATUS_LABEL[state]}
        </p>
        {error && <p className="mt-1 text-center text-[10px] text-red-300">{error}</p>}
      </div>

      <div className="flex items-center justify-center gap-4 border-t border-white/10 px-5 py-4">
        {isIncoming ? (
          <>
            <button
              onClick={onReject}
              title="Decline"
              className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500 transition-colors hover:bg-red-600"
            >
              <PhoneOff className="h-5 w-5" />
            </button>
            <button
              onClick={onAccept}
              title="Answer"
              className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] transition-colors hover:bg-[#1DA851]"
            >
              <Phone className="h-5 w-5" />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onToggleMute}
              title={muted ? 'Unmute' : 'Mute'}
              className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
                muted ? 'bg-white/30 hover:bg-white/40' : 'bg-white/15 hover:bg-white/25'
              }`}
            >
              {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
            <button
              onClick={onHangUp}
              title="Hang up"
              className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500 transition-colors hover:bg-red-600"
            >
              <PhoneOff className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
